export interface SessionTimeoutConfig {
  inactivityTimeout: number; // Minutes of inactivity before auto-logout
  absoluteTimeout: number; // Hours of total session time before forced re-auth
  warningBeforeTimeout: number; // Seconds before timeout to show warning
}

class SessionTimeoutService {
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private absoluteTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionStartTime: number | null = null;
  private lastActivityTime: number | null = null;
  
  private config: SessionTimeoutConfig = {
    inactivityTimeout: 30, // 30 minutes default
    absoluteTimeout: 8, // 8 hours default
    warningBeforeTimeout: 60, // 1 minute warning
  };

  private onTimeoutCallback: (() => void) | null = null;
  private onWarningCallback: ((secondsRemaining: number) => void) | null = null;

  constructor() {
    this.setupActivityListeners();
  }

  /**
   * Configure session timeout behavior
   */
  public configure(config: Partial<SessionTimeoutConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Start session tracking
   */
  public startSession(rememberMe: boolean = false): void {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();

    // Adjust timeouts for remember-me sessions
    if (rememberMe) {
      // For remember-me: 2 hours inactivity, 7 days absolute
      this.config.inactivityTimeout = 120; // 2 hours
      this.config.absoluteTimeout = 168; // 7 days
    } else {
      // Default for admin: 30 minutes inactivity, 8 hours absolute
      this.config.inactivityTimeout = 30;
      this.config.absoluteTimeout = 8;
    }

    this.scheduleInactivityTimeout();
    this.scheduleAbsoluteTimeout();

    console.log(`[Session Timeout] Session started with config:`, {
      inactivityTimeout: this.config.inactivityTimeout,
      absoluteTimeout: this.config.absoluteTimeout,
      rememberMe,
    });
  }

  /**
   * Stop session tracking and clear all timers
   */
  public stopSession(): void {
    this.clearAllTimers();
    this.sessionStartTime = null;
    this.lastActivityTime = null;
    console.log('[Session Timeout] Session stopped');
  }

  /**
   * Record user activity and reset inactivity timer
   */
  public recordActivity(): void {
    this.lastActivityTime = Date.now();
    this.scheduleInactivityTimeout();
  }

  /**
   * Set callback for session timeout
   */
  public onTimeout(callback: () => void): () => void {
    this.onTimeoutCallback = callback;
    
    // Return cleanup function
    return () => {
      this.onTimeoutCallback = null;
    };
  }

  /**
   * Set callback for timeout warning
   */
  public onWarning(callback: (secondsRemaining: number) => void): () => void {
    this.onWarningCallback = callback;
    
    // Return cleanup function
    return () => {
      this.onWarningCallback = null;
    };
  }

  /**
   * Schedule inactivity timeout
   */
  private scheduleInactivityTimeout(): void {
    this.clearInactivityTimer();
    
    const timeoutMs = this.config.inactivityTimeout * 60 * 1000;
    const warningMs = Math.max(0, timeoutMs - (this.config.warningBeforeTimeout * 1000));

    // Schedule warning
    if (warningMs > 0 && this.onWarningCallback) {
      this.warningTimer = setTimeout(() => {
        const remainingSeconds = this.config.warningBeforeTimeout;
        console.log(`[Session Timeout] Warning: ${remainingSeconds}s until logout due to inactivity`);
        this.onWarningCallback?.(remainingSeconds);
      }, warningMs);
    }

    // Schedule timeout
    this.inactivityTimer = setTimeout(() => {
      console.log('[Session Timeout] Inactivity timeout reached');
      this.handleTimeout('inactivity');
    }, timeoutMs);
  }

  /**
   * Schedule absolute session timeout
   */
  private scheduleAbsoluteTimeout(): void {
    this.clearAbsoluteTimer();
    
    if (!this.sessionStartTime) return;

    const timeoutMs = this.config.absoluteTimeout * 60 * 60 * 1000;
    const elapsedMs = Date.now() - this.sessionStartTime;
    const remainingMs = Math.max(0, timeoutMs - elapsedMs);

    this.absoluteTimer = setTimeout(() => {
      console.log('[Session Timeout] Absolute session timeout reached');
      this.handleTimeout('absolute');
    }, remainingMs);

    console.log(`[Session Timeout] Absolute timeout scheduled for ${Math.round(remainingMs / 1000 / 60)} minutes`);
  }

  /**
   * Handle timeout event
   */
  private handleTimeout(reason: 'inactivity' | 'absolute'): void {
    console.log(`[Session Timeout] Session timeout: ${reason}`);
    
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('session-timeout', {
        detail: { reason }
      }));
    }

    this.onTimeoutCallback?.();
  }

  /**
   * Setup activity listeners for user interactions
   */
  private setupActivityListeners(): void {
    if (typeof window === 'undefined') return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity recording to avoid excessive calls
    let activityThrottle: ReturnType<typeof setTimeout> | null = null;
    const throttledRecordActivity = () => {
      if (!activityThrottle) {
        this.recordActivity();
        activityThrottle = setTimeout(() => {
          activityThrottle = null;
        }, 10000); // Record activity at most once per 10 seconds
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledRecordActivity, { passive: true } as any);
    });
  }

  /**
   * Clear inactivity timer
   */
  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Clear absolute timer
   */
  private clearAbsoluteTimer(): void {
    if (this.absoluteTimer) {
      clearTimeout(this.absoluteTimer);
      this.absoluteTimer = null;
    }
  }

  /**
   * Clear all timers
   */
  private clearAllTimers(): void {
    this.clearInactivityTimer();
    this.clearAbsoluteTimer();
  }

  /**
   * Get session status for debugging
   */
  public getStatus(): {
    sessionStartTime: number | null;
    lastActivityTime: number | null;
    sessionDuration: number;
    timeSinceActivity: number;
    config: SessionTimeoutConfig;
  } {
    const now = Date.now();
    return {
      sessionStartTime: this.sessionStartTime,
      lastActivityTime: this.lastActivityTime,
      sessionDuration: this.sessionStartTime ? now - this.sessionStartTime : 0,
      timeSinceActivity: this.lastActivityTime ? now - this.lastActivityTime : 0,
      config: this.config,
    };
  }
}

// Export singleton instance
export const sessionTimeoutService = new SessionTimeoutService();
